import { authProviders } from '@cx-web/repub-config';

export const events = {
  ACCOUNT_CREATED: 'account_created',
  ACCOUNT_CREATED_ATTEMPT: 'account_created_attempt',
  ACCOUNT_CREATED_ERROR: 'account_created_error',
  LOG_IN: 'login_complete',
  LOG_IN_ATTEMPT: 'login_attempt',
  LOG_IN_ERROR: 'login_error',
  LOG_OUT: 'logout_complete',
  SOCIAL_ATTEMPT: 'sso_attempt',
  SOCIAL_COMPLETE: 'sso_complete',
} as const;

export type Event = (typeof events)[keyof typeof events];

export const actions = {
  LOGIN: 'Submit Normal Login',
  LOGIN_ERROR: 'Login Error',
  LOGIN_SUCCESS: 'Login Success',
  LOGOUT_SUCCESS: 'Logout Success',
  SIGN_UP: 'Submit Normal Sign Up',
  SIGN_UP_ERROR: 'Account Created Error',
  SIGN_UP_SUCCESS: 'Account Created',
  SOCIAL_AUTH: 'Social Authentication',
} as const;

export type Action = (typeof actions)[keyof typeof actions];

export const views = {
  LOGIN: 'Login View',
  LOGOUT: 'Logout View',
  RESET_PASSWORD: 'Reset Password View',
  SIGN_UP: 'Sign Up View',
  SOCIAL_AUTH: 'Social Auth View',
} as const;

export type View = (typeof views)[keyof typeof views];

export const providers = {
  GOOGLE: 'google',
} as const;

export type OAuth2Provider = (typeof providers)[keyof typeof providers];

/**
 * Short-lived cookie the OAuth callback writes onto its 302 so the landing page can report the
 * completed login/signup to GA4 itself. Shared client/server, hence here rather than beside the
 * other auth cookies in `server/cookies.server.ts`.
 */
export const SSO_RESULT_COOKIE = 'myoc-sso-result';

export type SsoResult = {
  provider: string;
  userId?: string;
  /** `views.LOGIN` or `views.SIGN_UP` — GTM splits `sso_complete` into its login/signup tags on this. */
  view: View;
};

export { authProviders };

/**
 * New-password policy, mirroring the myoddschecker backend (`ValidationUtils`): length is bounded
 * by `PASSWORD_MIN_LENGTH` / `PASSWORD_MAX_LENGTH` and there is no composition requirement — the
 * API accepts a password with no digit and one with no letter, so requiring either here would
 * reject passwords the backend is happy to store.
 *
 * Applies to the flows that set a NEW password (registration, change, reset). It deliberately does
 * not apply to login, which the backend validates on charset alone (`validatePassword`, no length
 * check) so legacy accounts whose password predates this policy can still sign in — keep login
 * validators at "non-empty".
 *
 * The backend's third term, `fitsStorage`, is deliberately not mirrored. It caps a new password at
 * `PasswordService.maxStorablePasswordLength()`, which is unbounded while the modern hashing scheme
 * is in use and 31 bytes otherwise. `argon2_password_enabled` is on in production, so the effective
 * ceiling is `PASSWORD_MAX_LENGTH` and there is nothing to mirror. Revisit only if that flag is
 * rolled back: this would then accept passwords the API rejects.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;

/**
 * Mirrors the backend's `ValidationUtils.PASSWORD_PATTERN` character class. The backend applies it
 * to new passwords too — `isNewPasswordAcceptable` is `satisfiesLengthPolicy && fitsStorage &&
 * validatePassword`, and `validatePassword` is this charset — so a password containing anything
 * outside it (a space, `é`) is rejected with AA101. Checking only length here would leave the same
 * client/server split this policy exists to close, just in the opposite direction.
 *
 * Note this is a permitted-character set, not a composition rule: nothing requires a letter or a
 * digit, and the symbols below are all accepted.
 */
const PASSWORD_CHARSET = /^[A-Za-z0-9@.,!?#£$*_\-+=/~|]+$/;

export function isPasswordAcceptable(value: unknown): boolean {
  const password = typeof value === 'string' ? value : '';
  return (
    password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH && PASSWORD_CHARSET.test(password)
  );
}

/**
 * Fallback copy for `CX_AUTH_ERROR_PASSWORD_INVALID`, worded to match the backend's AA101
 * (`API_PASSWORD_STRENGTH_ERROR`) so a client-side rejection and a server-side one read the same.
 */
export const PASSWORD_INVALID_MESSAGE = `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`;

export const AUTH_ERROR_LABEL_KEYS = {
  AA112: 'CX_AUTH_ERROR_PASSWORD_BREACHED',
  AL103: 'API_PASSWORD_RESET_REQUIRED_ERROR',
};

export function resolvePasswordErrorMessage(
  response: { message: string; messageCode: string },
  labels: Record<string, string>,
): string {
  const labelKey = (AUTH_ERROR_LABEL_KEYS as Record<string, string>)[response.messageCode];
  return (labelKey && labels[labelKey]) || response.message;
}

export function isView(view: string): view is keyof typeof views {
  return view in views;
}

export function isAuthProvider(provider: string): provider is keyof typeof authProviders {
  return provider in authProviders;
}
